import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createStore, applyMiddleware, compose } from 'redux';
import rootReducer from './rootReducer';
import rootSaga from './rootSaga';
import 'firebase/auth';
import { BrowserRouter as Router, Switch, useLocation } from 'react-router-dom';
import createSagaMiddleware from 'redux-saga';
import 'antd/dist/antd.css';
import PageHeader from './components/PageHeader';
import ScrollToTop from './scrollToTop';
import PageFooter from './components/PageFooter';
import './index.css';
import { loadUser } from './ducks/Auth/actions';
import {
  getScheduledNotifications,
  toggleInfoModal,
} from './ducks/Global/actions';
import AppShell from './components/AppShell';
import { AuthProvider } from './components/AuthContext';
import { Button } from 'antd';
// import HttpsRedirect from "react-https-redirect";
import IndexRoute from './IndexRoute';
import MetasForPage from './MetasForPage';
import MenuFooter from './components/MenuFooter';
import { createUserSetting } from './ducks/Setting/actions';
import { TopBanner } from './components/TopBanner';
import { AchievementModal } from './components/AchievementModal';
import MeasurementModal from './components/MeasurementModal';
import ReusableInfoModal from './components/Modal/ReusableInfoModal';
import ThemeProvider from './components/ThemeProvider';
import { SubdomainBrandProvider } from './components/SubdomainBrandProvider';
import NotificationPermissionModal from './components/NotificationPermissionModal';
import fcmNotificationService from './services/fcmNotificationService';
import { isTourActive, TOUR_CLOSED_EVENT } from './components/TrainerTour';
import {
  isNotificationPromptDismissed,
  dismissNotificationPrompt,
} from './services/notificationPrompt';

const sagaMiddleware = createSagaMiddleware();
const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

export const store = createStore(
  rootReducer,
  composeEnhancers(applyMiddleware(sagaMiddleware))
);
sagaMiddleware.run(rootSaga);

// No analytics or session recording: the app shows clients' health data,
// and the privacy policy (landing/privacy.html) promises no trackers.

document.addEventListener('wheel', () => {
  if (document.activeElement.type === 'number') {
    document.activeElement.blur();
  }
});

const App = () => {
  const [authVisible, setAuthVisible] = useState(false);
  const [authInfo, setAuthInfo] = useState({});
  const dispatch = useDispatch();
  const auth = useSelector((store) => store.auth);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const isRegistration = useSelector((state) => state.auth.isRegistration);
  const showTopHeader = useSelector((state) => state.global.showTopHeader);
  const topHeaderContent = useSelector(
    (state) => state.global.topHeaderContent
  );
  const achievementProps = useSelector(
    (state) => state.global.achievementProps
  );
  const showMeasurementModal = useSelector(
    (state) => state.global.showMeasurementModal
  );
  const measurementModalData = useSelector(
    (state) => state.global.measurementModalData
  );
  const measurementModalRole = useSelector(
    (state) => state.global.measurementModalRole
  );
  const measurementModalIsMetric = useSelector(
    (state) => state.global.measurementModalIsMetric
  );
  const measurementModalWeightObj = useSelector(
    (state) => state.global.measurementModalWeightObj
  );
  const infoModal = useSelector((state) => state.global.infoModal);
  const [windowWidth, setWindowWidth] = useState(null);
  const [showNotificationModal, setShowNotificationModal] = useState(false);

  const updateDimensions = () => {
    setWindowWidth(window.innerWidth);
  };

  useEffect(() => {
    const blurOnScroll = () => {
      const el = document.activeElement;
      if (
        el &&
        el.tagName === 'INPUT' &&
        (el.type === 'number' ||
          el.getAttribute('inputmode') === 'decimal' ||
          el.classList.contains('ant-input-number-input'))
      ) {
        el.blur();
      }
    };

    document.addEventListener('wheel', blurOnScroll);
    return () => {
      document.removeEventListener('wheel', blurOnScroll);
    };
  }, []);

  useEffect(() => {
    updateDimensions();
    window.addEventListener('resize', updateDimensions);

    // Safely initialize OneSignal
    if (window.OneSignal && typeof window.OneSignal.push === 'function') {
      window.OneSignal.push(function () {
        // Occurs when the user's subscription changes to a new value.
        window.OneSignal.on('subscriptionChange', (isSubscribed) => {
          dispatch(createUserSetting({ active: isSubscribed }));
        });
      });
    }

    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);

  useEffect(() => {
    if (!auth) {
      dispatch(loadUser());
    }
  }, []);

  // Set auth info when auth changes
  useEffect(() => {
    if (auth) {
      setAuthInfo(auth);
    }
  }, [auth]);

  // Decides whether to ask for web push permission — component must be
  // inside Router. Asks at most once per ACCOUNT (the answer is stored on the
  // user's Setting doc, not in this browser, so it holds across devices and
  // the per-coach subdomains) and only when asking can actually succeed:
  //   - no web push in this browser (iOS Safari outside a home-screen
  //     install) → never shown,
  //   - permission already granted → register this device's token silently,
  //   - permission denied → never nag (Settings → Notifications explains).
  const NotificationModalTrigger = ({ onShowModal }) => {
    const location = useLocation();
    const isAuth = useSelector((state) => state.auth.isAuthenticated);
    const userId = useSelector(
      (state) => state.auth.user?._id || state.auth.user?.id || null
    );
    const role = useSelector((state) => state.auth.user?.role);
    const hasCompletedAssessment = useSelector(
      (state) => !!state.auth.user?.isAssessmentCompleted
    );
    const isDashboardRoute = location.pathname.startsWith('/dashboard');
    const isTrainerPlus =
      role === 'trainer' ||
      role === 'organization_admin' ||
      role === 'platform_admin';
    const canAccessDashboard = isTrainerPlus || hasCompletedAssessment;
    // The account this mount has already decided for — the deps are all
    // primitives, so routine auth refreshes don't re-run the effect, and
    // navigating between dashboard pages doesn't re-schedule the prompt.
    const decidedFor = useRef(null);

    useEffect(() => {
      if (!isAuth || !userId || !onShowModal) return undefined;
      if (!isDashboardRoute || !canAccessDashboard) return undefined;
      if (decidedFor.current === userId) return undefined;
      decidedFor.current = userId;

      const supported =
        'Notification' in window &&
        'serviceWorker' in navigator &&
        'PushManager' in window;
      if (!supported || Notification.permission === 'denied') return undefined;
      if (Notification.permission === 'granted') {
        // Allowed in this browser already — just make sure this device's
        // token is registered. No modal.
        fcmNotificationService.initialize().catch(() => {});
        return undefined;
      }

      let seenHere = false;
      try {
        seenHere = !!localStorage.getItem(`hasSeenNotificationModal_${userId}`);
      } catch (_) {
        // storage blocked — fall through to the account-level answer
      }
      if (seenHere) return undefined;

      let cancelled = false;
      let timer = null;
      isNotificationPromptDismissed().then((dismissed) => {
        if (cancelled || dismissed) return;
        // Let the page settle before covering it. A coach's first-visit
        // studio tour goes first: wait for it to close, then ask.
        // Never stack on top of another dialog (an invite or a workout the
        // coach opened meanwhile): wait for it to close, checking every 2s.
        const anotherModalOpen = () =>
          [...document.querySelectorAll('.ant-modal-wrap')].some(
            (w) => w.style.display !== 'none'
          );
        const showWhenClear = () => {
          if (cancelled) return;
          if (anotherModalOpen()) {
            timer = setTimeout(showWhenClear, 2000);
            return;
          }
          onShowModal(true);
        };
        timer = setTimeout(() => {
          if (cancelled) return;
          if (isTourActive()) {
            window.addEventListener(
              TOUR_CLOSED_EVENT,
              () => {
                timer = setTimeout(showWhenClear, 1200);
              },
              { once: true }
            );
            return;
          }
          showWhenClear();
        }, 2500);
      });
      return () => {
        // Left the dashboard (or signed out) before the prompt showed: drop
        // it and let a later visit decide again.
        cancelled = true;
        if (timer) clearTimeout(timer);
        if (decidedFor.current === userId) decidedFor.current = null;
      };
    }, [isAuth, userId, isDashboardRoute, canAccessDashboard, onShowModal]);

    return null;
  };

  // The prompt was answered (enabled or skipped) → never ask this account
  // again. Saved on the account; localStorage is only a same-browser cache
  // that spares the round trip on the next load.
  const rememberPromptAnswered = () => {
    const userId = auth?.user?._id || auth?.user?.id;
    if (!userId) return;
    try {
      localStorage.setItem(`hasSeenNotificationModal_${userId}`, 'true');
    } catch (_) {
      // storage blocked — the account-level flag still covers it
    }
    dismissNotificationPrompt().catch(() => {});
  };

  return (
    <AuthProvider
      isRegistration={isRegistration}
      isAuthenticated={isAuthenticated}
      loadUser={() => dispatch(loadUser())}
    >
      <SubdomainBrandProvider>
      <ThemeProvider>
        <Router>
          <MetasForPage loc={window.location.pathname} />

          <div className='app'>
            <ScrollToTop />
            <div className='content-wrap'>
              <AppShell>
                {achievementProps && achievementProps.isOpen && (
                  <AchievementModal achievementProps={achievementProps} />
                )}
                {showMeasurementModal && (
                  <MeasurementModal
                    showModal={showMeasurementModal}
                    data={measurementModalData}
                    role={measurementModalRole}
                    isMetric={measurementModalIsMetric}
                    weightObj={measurementModalWeightObj}
                  />
                )}
                {infoModal.visible && (
                  <ReusableInfoModal
                    visible={infoModal.visible}
                    onClose={() => dispatch(toggleInfoModal(false))}
                    title={infoModal.title}
                    icon={infoModal.icon}
                    width={infoModal.width}
                    buttonText={infoModal.buttonText}
                    className={infoModal.className}
                  >
                    {infoModal.content}
                  </ReusableInfoModal>
                )}
                {showTopHeader && <TopBanner content={topHeaderContent} />}
                {/* PageHeader still renders the landing/marketing header on
                    non-dashboard routes; its dashboard branch is replaced by
                    AppShell's sticky Topbar. */}
                <PageHeader
                  authVisible={authVisible}
                  setAuthVisible={setAuthVisible}
                />
                <NotificationModalTrigger
                  onShowModal={setShowNotificationModal}
                />
                <NotificationPermissionModal
                  visible={showNotificationModal}
                  // Both outcomes (enabled / skipped) end in onClose.
                  onClose={() => {
                    setShowNotificationModal(false);
                    rememberPromptAnswered();
                  }}
                  onComplete={() => {}}
                />
                <Switch>
                  <IndexRoute setAuthVisible={setAuthVisible} />
                </Switch>
                <PageFooter />
                <Button className='sticky-btn' />
              </AppShell>
            </div>
          </div>
        </Router>
      </ThemeProvider>
      </SubdomainBrandProvider>
    </AuthProvider>
  );
};

export default App;
